import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import api from "config/api";
import { format } from "date-fns";
import moment from "moment";
import { NavigateFunction } from "react-router-dom";
import { toast } from "react-toastify";
import { AppDispatch } from "store";
import {
  CompanyData,
  ProductType,
  SettingType,
  SideBarItem,
  TaxesType,
  TimeItem,
} from "utils/types";

const sanitizeCartPayload = (value: any): any => {
  const seen = new WeakMap<object, any>();
  const omitKeys = new Set([
    "sourceDealPizza",
    "existingFullNote",
    "existingPizzaSize",
    "existingSizePrice",
    "existingPizzaCrust",
    "existingSpiceLevel",
    "existingSubTotal",
  ]);

  const visit = (input: any): any => {
    if (input === null || typeof input !== "object") return input;
    if (seen.has(input)) return seen.get(input);

    if (Array.isArray(input)) {
      const arr = input.map(visit);
      seen.set(input, arr);
      return arr;
    }

    const out: any = {};
    seen.set(input, out);

    Object.keys(input).forEach((key) => {
      if (omitKeys.has(key)) return;
      out[key] = visit(input[key]);
    });

    return out;
  };

  return visit(value);
};

const toNumber = (value: any, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const buildLocalOrderDateTime = (onDate: string, onTime: string) => {
  const [year, month, day] = String(onDate || "")
    .split("-")
    .map((value) => Number(value));
  const [hour, minute] = String(onTime || "")
    .split(":")
    .map((value) => Number(value));

  return new Date(
    Number.isFinite(year) ? year : 0,
    Number.isFinite(month) ? month - 1 : 0,
    Number.isFinite(day) ? day : 1,
    Number.isFinite(hour) ? hour : 0,
    Number.isFinite(minute) ? minute : 0,
    0,
    0
  );
};

const formatLocalOrderDateTime = (onDate: string, onTime: string) => {
  const localDate = buildLocalOrderDateTime(onDate, onTime);
  return moment(localDate).format("YYYY-MM-DDTHH:mm:ssZ");
};

const normalizeModifierPayload = (modifier: any = {}) => ({
  modifierId: toNumber(modifier?.modifierId || modifier?.pizzaModifierId),
  quantity: toNumber(modifier?.quantity, 1),
  unitPrice: toNumber(modifier?.unitPrice),
  subTotal: toNumber(modifier?.subTotal),
  description: String(
    modifier?.description || modifier?.pizzaModifierName || modifier?.modifierName || ""
  ),
  tax1: toNumber(modifier?.tax1),
  tax2: toNumber(modifier?.tax2),
  tax3: toNumber(modifier?.tax3),
  totalValue: toNumber(
    modifier?.totalValue ??
      toNumber(modifier?.subTotal) +
        toNumber(modifier?.tax1) +
        toNumber(modifier?.tax2) +
        toNumber(modifier?.tax3)
  ),
  note: String(modifier?.note || ""),
  pizzaRegion: toNumber(modifier?.pizzaRegion, 1),
  defaultTopping: toNumber(modifier?.defaultTopping),
});

const normalizePizzaCrustPayload = (pizzaCrust: any = null) =>
  pizzaCrust
    ? {
        crustId: toNumber(pizzaCrust?.crustId),
        crustName: String(pizzaCrust?.crustName || ""),
        crustPrice: toNumber(pizzaCrust?.crustPrice),
      }
    : null;

const normalizePizzaSizePayload = (pizzaSize: any = null) =>
  pizzaSize
    ? {
        pizzasizeId: toNumber(pizzaSize?.pizzasizeId || pizzaSize?.sizeId),
        size: String(pizzaSize?.size || pizzaSize?.sizeName || ""),
      }
    : null;

const normalizeSpiceLevelPayload = (spiceLevel: any = null) =>
  spiceLevel
    ? {
        spiceId: toNumber(spiceLevel?.spiceId, 1),
        spicename: String(spiceLevel?.spicename || spiceLevel?.modifierName || ""),
      }
    : null;

const buildPreferenceNoteLines = (pizzaSize: any = null, spiceLevel: any = null) => {
  const sizeLabel = String(pizzaSize?.size || pizzaSize?.sizeName || "").trim();
  const spiceLabel = String(
    spiceLevel?.spicename || spiceLevel?.modifierName || spiceLevel || ""
  ).trim();

  return [
    sizeLabel ? `Size: ${sizeLabel}` : "",
    spiceLabel ? `Spice: ${spiceLabel}` : "",
  ].filter(Boolean);
};

const appendPreferenceLinesToNote = (note: any, pizzaSize: any = null, spiceLevel: any = null) => {
  const baseNote = String(note || "").trim();
  const baseLines = baseNote
    ? baseNote
        .split("\n")
        .map((line) => line.trim())
        .filter(Boolean)
    : [];
  const filteredBaseLines = baseLines.filter(
    (line) => !line.startsWith("Size: ") && !line.startsWith("Spice: ")
  );

  return [...buildPreferenceNoteLines(pizzaSize, spiceLevel), ...filteredBaseLines]
    .filter(Boolean)
    .join("\n");
};

const normalizeDealPizzaPayload = (dealPizza: any = {}) => {
  const modifiers = Array.isArray(dealPizza?.modifiers)
    ? dealPizza.modifiers.map(normalizeModifierPayload)
    : [];
  const pizzaSize = normalizePizzaSizePayload(dealPizza?.pizzaSize);
  const spiceLevel = normalizeSpiceLevelPayload(dealPizza?.spiceLevel);

  return {
    productId: toNumber(dealPizza?.productId || dealPizza?.id || dealPizza?.config?.id),
    productCode: String(
      dealPizza?.productCode ||
        dealPizza?.config?.pizzaProductCode ||
        dealPizza?.pizzaName ||
        dealPizza?.config?.pizzaName ||
        dealPizza?.productName ||
        dealPizza?.fullDescription ||
        ""
    ),
    quantity: toNumber(dealPizza?.quantity, 1),
    totalUnitPrice: toNumber(
      dealPizza?.totalUnitPrice ?? dealPizza?.price ?? dealPizza?.subTotal
    ),
    discAmt: toNumber(dealPizza?.discAmt),
    discperc: toNumber(dealPizza?.discperc),
    subTotal: toNumber(dealPizza?.subTotal),
    fullDescription: String(
      dealPizza?.fullDescription ||
        dealPizza?.pizzaName ||
        dealPizza?.config?.pizzaName ||
        dealPizza?.productName ||
        ""
    ),
    taxId1: toNumber(dealPizza?.taxId1),
    taxId2: toNumber(dealPizza?.taxId2),
    taxId3: toNumber(dealPizza?.taxId3),
    taxRate1: toNumber(dealPizza?.taxRate1),
    taxRate2: toNumber(dealPizza?.taxRate2),
    taxRate3: toNumber(dealPizza?.taxRate3),
    totalTax1: toNumber(dealPizza?.totalTax1),
    totalTax2: toNumber(dealPizza?.totalTax2),
    totalTax3: toNumber(dealPizza?.totalTax3),
    itemTotal: toNumber(dealPizza?.itemTotal),
    note: appendPreferenceLinesToNote(
      dealPizza?.note || dealPizza?.config?.note || "",
      pizzaSize,
      spiceLevel
    ),
    isPizzaDeal: toNumber(
      dealPizza?.isPizzaDeal ?? dealPizza?.isDeal ?? 0
    ),
    isPizzaItem: toNumber(
      dealPizza?.isPizzaItem ?? dealPizza?.isPizza ?? 1
    ),
    isSpecilityPizza: toNumber(
      dealPizza?.isSpecilityPizza ?? dealPizza?.isSpecialityPizza ?? 1
    ),
    isCreateyourown: toNumber(
      dealPizza?.isCreateyourown ?? dealPizza?.isCreateYourOwn ?? 0
    ),
    pizzaCrust: normalizePizzaCrustPayload(dealPizza?.pizzaCrust),
    pizzaSize,
    spiceLevel,
    modifiers,
  };
};

const normalizeOrderDetailPayload = (item: any = {}) => {
  const selectedDealPizzas = Array.isArray(item?.selectedDealPizzas)
    ? item.selectedDealPizzas
    : Array.isArray(item?.data?.selectedDealPizzas)
    ? item.data.selectedDealPizzas
    : [];
  const modifiers = Array.isArray(item?.modifiers)
    ? item.modifiers.map(normalizeModifierPayload)
    : [];
  const pizzaSize = normalizePizzaSizePayload(item?.pizzaSize);
  const spiceLevel = normalizeSpiceLevelPayload(item?.spiceLevel);

  return {
    id: toNumber(item?.id || item?.productId),
    productId: toNumber(item?.productId || item?.id),
    productCode: String(item?.productCode || ""),
    fullDescription: String(item?.fullDescription || item?.description || ""),
    quantity: toNumber(item?.quantity, 1),
    price: toNumber(item?.price),
    variationsPrice: toNumber(item?.variationsPrice),
    totalUnitPrice: toNumber(item?.totalUnitPrice ?? item?.price ?? item?.subTotal),
    subTotal: toNumber(item?.subTotal),
    itemTotal: toNumber(item?.itemTotal),
    taxId1: toNumber(item?.taxId1),
    taxId2: toNumber(item?.taxId2),
    taxId3: toNumber(item?.taxId3),
    note: appendPreferenceLinesToNote(
      item?.note || item?.data?.config?.note || "",
      pizzaSize,
      spiceLevel
    ),
    totalTax1: toNumber(item?.totalTax1),
    totalTax2: toNumber(item?.totalTax2),
    totalTax3: toNumber(item?.totalTax3),
    status: String(item?.status || "new"),
    modifiers,
    isDeal: toNumber(item?.isDeal),
    isPizza: toNumber(item?.isPizza),
    isCreateYourOwn: toNumber(item?.isCreateYourOwn),
    taxRate1: toNumber(item?.taxRate1),
    taxRate2: toNumber(item?.taxRate2),
    taxRate3: toNumber(item?.taxRate3),
    pizzaCrust: normalizePizzaCrustPayload(item?.pizzaCrust),
    selectedDealPizzas: selectedDealPizzas.map(normalizeDealPizzaPayload),
    pizzaSize,
    spiceLevel,
  };
};

type InititalStateType = {
  loading: boolean;
  cart: any[];
  checkoutData: any;
  pizzaList: any[];
  userData: any;
  taxes: TaxesType[];
  settings: SettingType;
  showCart: boolean;
  showUserDetailModel: boolean;
  showDrawer: boolean;
  companyData?: CompanyData;
  sideBarMenu: SideBarItem[];
  selectedProduct: ProductType | undefined;
  validTimes: TimeItem[];
  gestData: any;
  deliveryType: string;
  showGlobalSearchModal: boolean;
  isSelectGuestUser: boolean;
  guestUserData: {
    address: string;
    tip: number;
    clientId: number;
    suitNo: string;
    buzzerNo: string;
    roomNo: string;
    firstName: string;
    lastName: string;
    address1: string;
    phoneNo: string;
    email: string;
    password: string;
    confirmPassword: string;
    onDate: string;
    onTime: string;
    selectedAddress: string;
  };
  cartBtnLoading: Boolean;
};

const getApiErrorMessage = (error: any, fallback = "Something went wrong") => {
  const responseData = error?.response?.data;
  const status = error?.response?.status;
  const requestUrl = error?.config?.url || error?.request?.responseURL || "";

  if (status === 404) {
    return `API route not found (404): ${requestUrl || "/api/OrderSubmition"}`;
  }

  if (typeof responseData === "string" && responseData.trim()) {
    return responseData;
  }

  if (typeof responseData?.message === "string" && responseData.message.trim()) {
    return responseData.message;
  }

  if (typeof responseData?.title === "string" && responseData.title.trim()) {
    return responseData.title;
  }

  if (typeof error?.message === "string" && error.message.trim()) {
    return error.message;
  }

  return fallback;
};

const getCachedUserData = () => {
  if (typeof window === "undefined") return undefined;

  try {
    const raw = window.localStorage.getItem("userData");
    return raw ? JSON.parse(raw) : undefined;
  } catch {
    return undefined;
  }
};

/*
OnlineOrderingStatus ✅
ClosePickupOrder✅
CloseDeliveryOrder✅
PickupOnlinePayment✅
DeliveryOnlineOrder✅
DontPayAtStorePickup✅
DontPayAtStoreDelivery✅
ShowCompanyName✅
ShowLogo 🔴
ApplyTakeOutDeliveryPrice🔴
ApplyDeliveryChargesAsKm 🔴
DontDisplayTipOption ✅
MinimumDeliveryOrder 🔴
OnlineDeliveryBoundry 🔴
AllowDiscountOnDelivery ✅
AllowDiscountOnPickup✅
OnlineSpecialDiscount ✅
DisplayLocationOnCheckout✅

extra flags 
AllowFutureDate
*/
const initialSetting = {
  OnlineCombineLineQty: 0,
  AllowFutureDate: 1,
  MapAPIKeySetting: "",
  EnableGiftcardPromo: 0,
  DisplayLocationOnCheckout: 0,
  AllowDiscountOnDelivery: 1,
  AllowDiscountOnPickup: 1,
  DontDisplayTipOption: 0,
  ApplyTakeOutDeliveryPrice: 0,
  DontDisplayPizzaSubLevel: 0,
  DisplayDefaultCrustOnline: 0,
  OnlineSpecialDiscount: 1,
  OnlineDeliveryBoundry: 0,
  ApplySpecialDiscountOnward: 0,
  MinimumDeliveryOrder: 0,
  DeliveryOrderOptions: 1,
  ReservationOrderOptions: 0,
  CloseReservationOrder: 0,
  DeptDisplayOption: "Both",
  ShowLogo: 0,
  ShowCompanyName: 1,
  PickupOrderOptions: 1,
  DontPayAtStorePickup: 1,
  DontPayAtStoreDelivery: 1,
  PaypalPickup: 0,
  PaypalDelivery: 0,
  AuthorizePickup: 0,
  AuthorizeDelivery: 0,
  MonerisPickup: 0,
  MonerisDelivery: 0,
  StripePickup: 0,
  StripeDelivery: 0,
  ElavonPickup: 0,
  ElavonDelivery: 0,
  TDPickup: 0,
  TDDelivery: 0,
  GlobalPickup: 0,
  GlobalDelivery: 0,
  CloverPickup: 0,
  CloverDelivery: 0,
  PickupOnlinePayment: 1,
  DeliveryOnlineOrder: 1,
  ApplyDeliveryChargesAsKm: 1,
  ApplySpecialDiscountAsTotal: 0,
  DisplayDefaultToppings: 0,
  OnlineOrderingStatus: 1,
  ClosePickupOrder: 1,
  CloseDeliveryOrder: 0,
  menuDirection: "",
  MinimumDeliveryOrderAmt: "",
  DefaultCrustName: "",
  showLogo: 0,
  themeColor: 0,
  itemPriceColor: 0,
  itemColor: 0,
  deptColor: 0,
  themeSelColor: 0,
  headerColor: 0,
  itemPriceBgColor: 0,
  clearCartOnBrowerExit: 0,

  // === ADDED BY SAMPATH – 2026-01-29 ===
  // New Header settings (Text color + Banner URL)
  // NOTE: URL is string, so it will stay string due to your merge logic: isNaN(+v) ? v : +v
  headerTextColor: 0,
  headerBannerUrl: "",
  // === END SAMPATH ===
};

const initialState: InititalStateType = {
  loading: true,
  cart: [],
  pizzaList: [],
  userData: getCachedUserData(),
  showCart: false,
  showUserDetailModel: false,
  showDrawer: false,
  taxes: [],
  settings: initialSetting,
  sideBarMenu: [],
  selectedProduct: undefined,
  checkoutData: undefined,
  validTimes: [],
  companyData: undefined,
  gestData: undefined,
  deliveryType: "",
  showGlobalSearchModal: false,
  isSelectGuestUser: false,
  guestUserData: {
    address: "",
    tip: 0,
    clientId: 0,
    suitNo: "",
    buzzerNo: "",
    roomNo: "",
    firstName: "",
    lastName: "",
    address1: "",
    phoneNo: "",
    email: "",
    password: "",
    confirmPassword: "",
    onDate: format(new Date(), "yyyy-MM-dd"),
    onTime: format(new Date(), "HH:mm"),
    selectedAddress: "home",
  },
  cartBtnLoading: false,
};

export const mainSlice = createSlice({
  name: "counter",
  initialState,
  reducers: {
    setInitialData: (state, action: PayloadAction<any>) => {
      state.loading = false;
      state.sideBarMenu = action.payload.categories;
      state.taxes = action.payload.taxes;
      state.companyData = action.payload.companyData;
      state.pizzaList = [];
      state.settings = {
        ...initialSetting,
        ...(Object.entries(action.payload.settings).reduce(
          (obj, [k, v]: any) => ({ ...obj, [k]: isNaN(+v) ? v : +v }),
          {}
        ) as any),
      };
    },
    setdeliveryType: (state, action: PayloadAction<any>) => {
      state.deliveryType = action.payload;
    },
    setSettings: (state, action: PayloadAction<any>) => {
      state.settings = action.payload;
    },
    setCheckoutData: (state, action: PayloadAction<any>) => {
      state.checkoutData = action.payload;
    },
    updateCheckoutData: (state, action: PayloadAction<any>) => {
      state.checkoutData = { ...state.checkoutData, ...action.payload };
    },
    setSelected: (state, action: PayloadAction<any>) => {
      state.selectedProduct = action.payload;
    },
    setPizzaList: (state, action: PayloadAction<any>) => {
      state.pizzaList = action.payload;
    },
    setUserData: (state, action: PayloadAction<any>) => {
      state.userData = action.payload;
      if (typeof window !== "undefined") {
        if (action.payload) {
          window.localStorage.setItem("userData", JSON.stringify(action.payload));
        } else {
          window.localStorage.removeItem("userData");
        }
      }
    },
    setAllCartData: (state, action: PayloadAction<any>) => {
      state.cart = action.payload || [];
    },
    setGestData: (state, action: PayloadAction<any>) => {
      state.gestData = action.payload;
    },
    setCartBtnLoading: (state, action: PayloadAction<Boolean>) => {
      state.cartBtnLoading = action.payload;
    },
    addToCart: (state, action: PayloadAction<any>) => {
      const userId = state?.userData?.clientId;
      const item = action.payload;
      if (item?.data.isEdit) {
        if (userId) {
          const sanitizedItem = sanitizeCartPayload(item);
          api
            .post("/api/Cart/UpdateCart", [
              {
                ...sanitizedItem,
                cartId: state.cart[item.data.index].cartId,
                userId,
              },
            ])
            .then(({ data }) => {
              console.log("Added to cart", data);
            })
            .catch((e) => {
              console.log(e);
            });
        }
        state.cart[item.data.index] = item;
      } else if (item) {
        if (userId) {
          const sanitizedItem = sanitizeCartPayload(item);
          api
            .post("/api/Cart/AddCart", [{ ...sanitizedItem, cartId: 0, userId }])
            .then(({ data }) => {
              if (data.orderId) {
                console.log("Added to cart", data);
              }
            })
            .catch((e) => {
              console.log(e);
            });
          state.cart.push(item);
        }
      }
    },
    setValidTimes: (state, action: PayloadAction<any>) => {
      state.validTimes = action.payload;
    },
    removeFromCart: (state, action: PayloadAction<number>) => {
      const userId = state?.userData?.clientId;
      let cartId = -1;
      state.cart = state.cart.filter((item, i) => {
        if (i !== action.payload) return true;
        cartId = item.cartId;
        return false;
      });

      if (userId && cartId !== -1) {
        api
          .post("/api/Cart/DeleteCart?id=" + cartId)
          .then(({ data }) => {
            console.log("deleted from cart", data);
          })
          .catch((e) => {
            console.log(e);
          });
      }
    },
    removeAllFromCart: (state) => {
      state.cart = [];
      const userId = state?.userData?.clientId;
      if (userId) {
        api
          .post("/api/Cart/DeleteUserCart?userid=" + userId)
          .then(({ data }) => {
            console.log("Delete entire cart", data);
          })
          .catch((e) => {
            console.log(e);
          });
      }
    },
    toggleLoading: (state) => {
      state.loading = !state.loading;
    },
    toggleCart: (state) => {
      state.showCart = !state.showCart;
    },
    toggleDrawer: (state) => {
      state.showDrawer = !state.showDrawer;
    },
    toggleUserDetailModel: (state) => {
      state.showUserDetailModel = !state.showUserDetailModel;
    },
    toggleGlobalSearchModal: (state) => {
      state.showGlobalSearchModal = !state.showGlobalSearchModal;
    },
    setIsGuestUser: (state, action: PayloadAction<boolean>) => {
      state.isSelectGuestUser = action.payload;
    },
    resetStates: (state) => {
      state.cart = [];
    },
  },
});

export const fetchInitialData =
  (navigate: NavigateFunction) =>
  async (dispatch: AppDispatch, getState: any) => {
    const clientId = getState()?.main?.userData?.clientId;

    dispatch(toggleLoading());
    api
      .get("/api/initialData")
      .then(({ data }) => {
        document.title = data.companyData.companyName;

        //! update logo dynamically
        // let link: any = document.querySelector("link[rel~='icon']");
        // if (!link) {
        //   link = document.createElement("link");
        //   link.rel = "icon";
        //   document.getElementsByTagName("head")[0].appendChild(link);
        // }
        // link.href = "https://stackoverflow.com/favicon.ico";
        const search = new URLSearchParams(window.location.search);
        if (!search.get("product")) {
          search.set("product", data.categories[0].id);
          navigate(
            {
              pathname: window.location.pathname,
              search: search.toString(),
            },
            { replace: true }
          );
        }
        dispatch(setInitialData(data));
      })
      .catch((e) => {
        toast.error(getApiErrorMessage(e, "Failed to load initial data"));
        console.error("Failed to load /api/initialData", e);
        dispatch(toggleLoading());
      });
    if (clientId) {
      api
        .get("/api/Cart/GetUserCart?userid=" + clientId)
        .then((res) => {
          dispatch(setAllCartData(res.data));
        })
        .catch((e) => {
          console.error("Failed to load user cart", e);
        });
    }
    api
      .get("/api/RestTime/GetResttimes")
      .then(({ data }) => {
        dispatch(setValidTimes(data));
      })
      .catch((e) => {
        console.error("Failed to load rest times", e);
      });
  };

export const placeOrder =
  ({
    tip,
    paymentType,
    setStage,
    deliveryCharge = 0,
    notes = "",
    setShowPopup,
    setOrderTotal,
  }: any) =>
  (dispatch: any, getState: any) => {
    const { checkoutData, cart } = getState().main;

    const taxes = cart.reduce(
      (obj: any, itm: any) => {
        if (itm.taxId1) {
          obj.taxId1 = 1;
          obj.totalTax1 = +(obj.totalTax1 + itm.totalTax1).toFixed(5);
        }
        if (itm.taxId2) {
          obj.taxId2 = 2;
          obj.totalTax2 = +(obj.totalTax2 + itm.totalTax2).toFixed(5);
        }
        if (itm.taxId3) {
          obj.taxId3 = 3;
          obj.totalTax3 = +(obj.totalTax3 + itm.totalTax3).toFixed(5);
        }

        return obj;
      },
      {
        taxId1: 0,
        taxId2: 0,
        taxId3: 0,
        totalTax1: 0,
        totalTax2: 0,
        totalTax3: 0,
      }
    );

    const subTotal =
      checkoutData.finalBill.find((i: any) => i.title === "SubTotal")?.amt ?? 0;
    const billTotal = +((checkoutData?.finalBill || []).reduce(
      (sum: number, item: any) => sum + Number(item?.amt || 0),
      0
    )).toFixed(2);
    const resolvedTip = +Number(tip ?? checkoutData?.tip ?? 0).toFixed(2);
    const resolvedDeliveryCharge = +Number(deliveryCharge || 0).toFixed(2);
    const finalOrderTotal = +(billTotal + resolvedDeliveryCharge).toFixed(2) + resolvedTip;

    const orderDetails = cart.map(normalizeOrderDetailPayload);

    const params = {
      clientId: checkoutData.clientId,
      clientName: checkoutData.firstName,
      paymentType,
      ...taxes,
      stationId: 0, // static
      subTotal,
      notes: notes,
      deliveryCharge: resolvedDeliveryCharge,
      orderTotal: +finalOrderTotal.toFixed(2),
      promoCode: "", // static
      serviceCharge: "", // static
      promoDiscount: 0, // static
      serviceFee: 0, // static
      bagFee: 0, // static
      tip: resolvedTip,
      id: 0, // static
      suitNo: "", // blank
      buzzerNo: "", // blank
      roomNo: "", // blank
      firstName: checkoutData.firstName,
      lastName: checkoutData.lastName,
      email: checkoutData.email,
      address: checkoutData.address,
      date: null, //null
      time: null, //null
      orderType: checkoutData.orderType,
      onDate: moment(checkoutData.onDate).format("MM/DD/YYYY"),
      onTime: checkoutData.onTime + ":00",
      orderDetails,
      onDateTime: formatLocalOrderDateTime(
        checkoutData.onDate,
        checkoutData.onTime
      ),
    };

    api
      .post("/api/OrderSubmition", params)
      .then(({ data }) => {
        setStage?.("CART");
        dispatch(setCheckoutData(undefined));
        dispatch(setSelected(undefined));
        // dispatch(setGestData(undefined));
        dispatch(toggleCart());
        dispatch(removeAllFromCart());
        // toast.success("Order Places Successfully!");
        setShowPopup?.(true);
        setOrderTotal?.(params.orderTotal);
      })
      .catch((e) => {
        setShowPopup?.(false);
        toast.error(getApiErrorMessage(e));
      });
  };
export const {
  toggleLoading,
  removeAllFromCart,
  toggleDrawer,
  setPizzaList,
  setInitialData,
  setUserData,
  setSelected,
  addToCart,
  removeFromCart,
  toggleCart,
  setAllCartData,
  setCheckoutData,
  setSettings,
  toggleUserDetailModel,
  updateCheckoutData,
  setValidTimes,
  setGestData,
  setdeliveryType,
  toggleGlobalSearchModal,
  setIsGuestUser,
  resetStates,
  setCartBtnLoading,
} = mainSlice.actions;

export default mainSlice.reducer;
